import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import type { OsobyPodnikatelDto } from '@gov-nx/api';
import {
	useCountryCodes,
	useEmailContactDetails,
	usePersonCommunicationData,
	usePhoneContactDetails,
	useServiceAuthorization,
	useUpdateContact,
} from '@gov-nx/core/hooks';
import { useFormBuilderWithConsent, useLocale } from '@gov-nx/core/service';
import type { Nullable, RequiredProperty } from '@gov-nx/core/types';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';
import { useBoolean } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type {
	NastaveniKontaktniUdajeROSItemContextValue,
	PersonalContactsConsentForm,
} from './context.types';
import { useROSContact } from './useROSContact';

const AUTHORIZATION_SERVICE_NAME = 'PODANI_KONTAKTNI_UDAJE_ROS';

export const NastaveniKontaktniUdajeROSItemContext =
	createContext<Nullable<NastaveniKontaktniUdajeROSItemContextValue>>(null);

type NastaveniKontaktniUdajeROSItemContextProviderProps = PropsWithChildren<{
	contact: RequiredProperty<OsobyPodnikatelDto, 'ico'>;
	code: PageCode;
}>;

export const NastaveniKontaktniUdajeROSItemContextProvider = ({
	contact,
	code,
	children,
}: NastaveniKontaktniUdajeROSItemContextProviderProps) => {
	const { t } = useLocale(code);
	const {
		reloadRosContact,
		getRosContact,
		isReloading: isRosContactReLoading,
		isLoading: isRosContactLoading,
	} = useROSContact();
	const communicationData = usePersonCommunicationData();
	const rosContact = getRosContact(contact.ico);
	const countryCodes = useCountryCodes();
	const isEditingEmail = useBoolean();
	const isEditingPhone = useBoolean();
	const isConsentDialogOpen = useBoolean();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const authContext = useAutorizaceContext();

	const onAuthorization = (
		autorizaceId: string,
		nonHighAuthorizationFlow: () => void
	) => {
		if (authContext) {
			authContext.onInitAuthorization(
				AUTHORIZATION_SERVICE_NAME,
				autorizaceId,
				nonHighAuthorizationFlow
			);
		} else {
			nonHighAuthorizationFlow();
		}
	};

	const updateContact = useUpdateContact({
		source: { source: 'ROS', ico: contact.ico },
		unverifiedEmail: rosContact?.emailOvereni,
		unverifiedPhone: rosContact?.telefonniCisloOvereni,
	});

	const isUpdating =
		updateContact.email.isUpdating || updateContact.phone.isUpdating;

	const consentFormDefinition = useFormBuilderWithConsent<
		PersonalContactsConsentForm,
		object
	>([], { isLowAuthorization, isDisabled: isUpdating });

	const emailContactDetails = useEmailContactDetails({
		updateContact: updateContact.email,
		refetch: reloadRosContact,
		isReloading: isRosContactReLoading,
		isEditing: isEditingEmail,
		unverifiedEmail: rosContact?.emailOvereni ?? undefined,
		emailVerified: rosContact?.email,
		tooltipText: t('kontakt.contact-info-tooltip-po'),
		isLowAuthorization,
		isAuthorizationEnforced,
		onAuthorization,
	});

	const phoneContactDetails = usePhoneContactDetails({
		updateContact: updateContact.phone,
		refetch: reloadRosContact,
		isReloading: isRosContactReLoading,
		countryCodes,
		isEditing: isEditingPhone,
		phoneVerified: rosContact?.telefonniCislo,
		unverifiedPhone: rosContact?.telefonniCisloOvereni ?? undefined,
		tooltipText: t('kontakt.contact-info-tooltip-po'),
		isLowAuthorization,
		isAuthorizationEnforced,
		onAuthorization,
	});

	const handleUsePersonalContacts = () => {
		if (communicationData.emailVerified) {
			emailContactDetails.onPersonalContactsUse(
				communicationData.emailVerified
			);
		}

		if (communicationData.phoneVerified) {
			phoneContactDetails.onPersonalContactsUse(
				communicationData.phoneVerified
			);
		}

		isConsentDialogOpen.setFalse();
		consentFormDefinition.formReset();
	};

	const canUsePersonalContacts =
		!!communicationData.emailVerified && !!communicationData.phoneVerified;

	return (
		<NastaveniKontaktniUdajeROSItemContext.Provider
			value={{
				code,
				initialState: {
					isLoading: !countryCodes.isFetched || isRosContactLoading,
					error: countryCodes.error ?? undefined,
				},
				contact,
				handleUsePersonalContacts,
				isUsingPersonalContactsLoading:
					updateContact.email.isUpdating || updateContact.phone.isUpdating,
				canUsePersonalContacts,
				email: emailContactDetails,
				phone: phoneContactDetails,
				personalContactsConsent: {
					isDialogOpen: isConsentDialogOpen,
					isLowAuthorization,
					isAuthorizationEnforced,
					formDefinition: consentFormDefinition,
					onSubmit: consentFormDefinition.formMethods.handleSubmit(
						handleUsePersonalContacts
					),
				},
			}}>
			{children}
		</NastaveniKontaktniUdajeROSItemContext.Provider>
	);
};

export const useNastaveniKontaktnichUdajuROSItemContext =
	(): NastaveniKontaktniUdajeROSItemContextValue =>
		useContext(
			NastaveniKontaktniUdajeROSItemContext
		) as NastaveniKontaktniUdajeROSItemContextValue;
